import { useState, type ReactNode } from 'react';

import type { SettingsSectionProps } from '../settings-types.js';
import { collisionMatrix, ignoreFromMatrix, isObj, layerList, setLayerCollision, type LayerPair } from '../settings-validate.js';

/**
 * Unity's Layer Collision Matrix: a triangle of checkboxes, one per layer
 * pair; unchecked pairs are written to `settings.physics.ignoreCollisions`.
 * Columns run from the last layer to the first, so each row starts at the
 * same column and the triangle shrinks downwards.
 */
export function CollisionMatrix({ value, settings, set }: SettingsSectionProps) {
  const physics = isObj(value) ? value : {};
  const layers = layerList(settings);
  const matrix = collisionMatrix(layers, physics.ignoreCollisions);
  const [hover, setHover] = useState<[number, number] | null>(null);
  const n = layers.length;
  const columns = [...layers.keys()].reverse();

  const write = (pairs: LayerPair[], label: string) => {
    const next = { ...physics };
    if (pairs.length > 0) next.ignoreCollisions = pairs;
    else delete next.ignoreCollisions;
    set(Object.keys(next).length > 0 ? next : undefined, label);
  };

  const all = (collide: boolean) =>
    write(
      ignoreFromMatrix(
        layers,
        layers.map(() => layers.map(() => collide)),
        physics.ignoreCollisions,
      ),
      collide ? 'Enable all layer collisions' : 'Disable all layer collisions',
    );

  return (
    <div className="collision-matrix">
      <div className="collision-matrix-head">
        <strong>Layer Collision Matrix</strong>
        <span className="collision-matrix-actions">
          <button type="button" className="ghost small" onClick={() => all(true)}>
            Enable All
          </button>
          <button type="button" className="ghost small" onClick={() => all(false)}>
            Disable All
          </button>
        </span>
      </div>
      <div className="collision-matrix-scroll">
        <div className="collision-matrix-grid" style={{ gridTemplateColumns: `minmax(64px, max-content) repeat(${n}, 18px)` }}>
          <span />
          {columns.map((j) => (
            <span key={`h${j}`} className={`collision-matrix-col${hover?.[1] === j ? ' is-hover' : ''}`} title={layers[j]}>
              <span>{layers[j]}</span>
            </span>
          ))}
          {layers.map((rowName, i) => (
            <Row
              key={rowName}
              name={rowName}
              hover={hover?.[0] === i}
              cells={columns.map((j, c) =>
                j < i ? (
                  <span key={c} />
                ) : (
                  <input
                    key={c}
                    type="checkbox"
                    className="collision-matrix-cell"
                    checked={matrix[i]![j]!}
                    title={`${rowName} ↔ ${layers[j]}`}
                    aria-label={`${rowName} collides with ${layers[j]}`}
                    onPointerEnter={() => setHover([i, j])}
                    onPointerLeave={() => setHover(null)}
                    onChange={(e) =>
                      write(
                        setLayerCollision(layers, physics.ignoreCollisions, rowName, layers[j]!, e.target.checked),
                        `${e.target.checked ? 'Enable' : 'Disable'} collision ${rowName} ↔ ${layers[j]}`,
                      )
                    }
                  />
                ),
              )}
            />
          ))}
        </div>
      </div>
      {n === 1 && <p className="card-note muted">Add layers in Tags &amp; Layers to choose which of them collide.</p>}
    </div>
  );
}

function Row({ name, hover, cells }: { name: string; hover: boolean; cells: ReactNode[] }) {
  return (
    <>
      <span className={`collision-matrix-row${hover ? ' is-hover' : ''}`} title={name}>
        {name}
      </span>
      {cells}
    </>
  );
}
